/**
 * Lectura de errores de la API de cría (FRONTEND_INTEGRATION.md §6).
 *
 * `detail` toma una de tres formas:
 * - string: 401, 403 y 404 de recurso único.
 * - { codigo, mensaje, ...extra }: errores de dominio (404 con IDs, 409, 422).
 * - [{ loc, msg, type }]: 422 de formato (FastAPI).
 */

export const TIPO_ERROR = Object.freeze({
  NO_HABILITADA: "no_habilitada",
  SIN_SESION: "sin_sesion",
  SIN_PERMISO: "sin_permiso",
  NO_ENCONTRADO: "no_encontrado",
  CONFLICTO_REVISION: "conflicto_revision",
  CONFLICTO: "conflicto",
  VALIDACION: "validacion",
  CANCELADO: "cancelado",
  RECUPERABLE: "recuperable",
});

const ETIQUETAS_CAMPO = {
  nombre: "Nombre",
  fecha_inicio_servicio: "Inicio de servicio",
  fecha_fin_servicio: "Fin de servicio",
  fecha_corte: "Fecha de corte",
  motivo: "Motivo",
  peso_kg: "Peso",
  fecha_pesaje: "Fecha de pesaje",
  hectareas: "Hectáreas",
  limite: "Límite",
  pagina: "Página",
  animal_ids: "Animales",
  campania_ids: "Campañas",
  ultimas_n: "Últimas campañas",
  revision: "Revisión",
};

export function esCancelacion(error) {
  return (
    error?.name === "CanceledError" ||
    error?.name === "AbortError" ||
    error?.code === "ERR_CANCELED"
  );
}

/** Extrae `{ status, detail }` de un error de axios (o de un objeto ya normalizado). */
function leerRespuesta(error) {
  const status = error?.response?.status ?? error?.status ?? null;
  const detail =
    error?.response?.data?.detail !== undefined
      ? error.response.data.detail
      : error?.detail;
  return { status, detail };
}

export function codigoError(error) {
  const { detail } = leerRespuesta(error);
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    return detail.codigo;
  }
  return undefined;
}

export function esFuncionNoHabilitada(error) {
  const { status } = leerRespuesta(error);
  return status === 404 && codigoError(error) === "funcion_no_habilitada";
}

function mensajeCampo(item) {
  const loc = Array.isArray(item?.loc) ? item.loc : [];
  const campo = loc.filter((p) => p !== "body" && p !== "query").join(".");
  const etiqueta = ETIQUETAS_CAMPO[loc[loc.length - 1]] || campo || "Dato";
  return { campo, mensaje: `${etiqueta}: valor inválido` };
}

/**
 * Normaliza un error de la API de cría.
 * @returns {{ tipo: string, status: number|null, codigo?: string, mensaje: string,
 *            campos: Array<{campo: string, mensaje: string}>, extra: object }}
 */
export function interpretarErrorCria(
  error,
  mensajePorDefecto = "No se pudo completar la operación"
) {
  if (esCancelacion(error)) {
    return {
      tipo: TIPO_ERROR.CANCELADO,
      status: null,
      mensaje: "",
      campos: [],
      extra: {},
    };
  }

  const { status, detail } = leerRespuesta(error);
  const base = { status, campos: [], extra: {} };

  if (Array.isArray(detail)) {
    const campos = detail.map(mensajeCampo);
    return {
      ...base,
      tipo: TIPO_ERROR.VALIDACION,
      mensaje: campos.map((c) => c.mensaje).join(" · ") || mensajePorDefecto,
      campos,
    };
  }

  if (detail && typeof detail === "object") {
    const { codigo, mensaje, ...extra } = detail;
    let tipo = TIPO_ERROR.RECUPERABLE;
    if (status === 404 && codigo === "funcion_no_habilitada")
      tipo = TIPO_ERROR.NO_HABILITADA;
    else if (status === 404) tipo = TIPO_ERROR.NO_ENCONTRADO;
    else if (status === 409 && codigo === "revision_desactualizada")
      tipo = TIPO_ERROR.CONFLICTO_REVISION;
    else if (status === 409) tipo = TIPO_ERROR.CONFLICTO;
    else if (status === 422) tipo = TIPO_ERROR.VALIDACION;
    else if (status === 403) tipo = TIPO_ERROR.SIN_PERMISO;
    return {
      ...base,
      tipo,
      codigo,
      mensaje: mensaje || mensajePorDefecto,
      extra,
    };
  }

  const texto = typeof detail === "string" && detail ? detail : null;
  if (status === 401)
    return {
      ...base,
      tipo: TIPO_ERROR.SIN_SESION,
      mensaje: texto || "La sesión venció",
    };
  if (status === 403)
    return {
      ...base,
      tipo: TIPO_ERROR.SIN_PERMISO,
      mensaje: texto || "No tenés permiso para esta acción",
    };
  if (status === 404)
    return {
      ...base,
      tipo: TIPO_ERROR.NO_ENCONTRADO,
      mensaje: texto || "No encontrado",
    };
  if (status === 422)
    return {
      ...base,
      tipo: TIPO_ERROR.VALIDACION,
      mensaje: texto || mensajePorDefecto,
    };
  if (status === 409)
    return {
      ...base,
      tipo: TIPO_ERROR.CONFLICTO,
      mensaje: texto || mensajePorDefecto,
    };
  return {
    ...base,
    tipo: TIPO_ERROR.RECUPERABLE,
    mensaje: texto || mensajePorDefecto,
  };
}
